import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Package, Search, Calendar, Shield, MapPin, Truck, CheckCircle, CircleDot, Circle, ChevronDown, ChevronUp, CreditCard, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TRACKING_STEPS = [
  { key: 'order_received', label: 'Order Received', desc: 'Your order has been placed' },
  { key: 'gear_prepared', label: 'Gear Prepared', desc: 'Your gear has been sanitized and packed' },
  { key: 'shipped', label: 'Shipped', desc: 'Your package has been shipped' },
  { key: 'in_transit', label: 'In Transit', desc: 'Your package is on its way' },
  { key: 'delivered', label: 'Delivered', desc: 'Your gear has been delivered' },
  { key: 'returned', label: 'Returned', desc: 'Gear returned and under inspection' },
  { key: 'completed', label: 'Completed', desc: 'Rental complete, deposit refunded' },
];

const DepositBadge = ({ status }) => {
  const config = {
    held: { label: 'Deposit Held', className: 'bg-amber-100 text-amber-700 border-amber-300' },
    refunded: { label: 'Deposit Refunded', className: 'bg-green-100 text-green-700 border-green-300' },
    under_review: { label: 'Under Review', className: 'bg-blue-100 text-blue-700 border-blue-300' },
    forfeited: { label: 'Forfeited', className: 'bg-red-100 text-red-700 border-red-300' },
    none: { label: 'No Deposit', className: 'bg-gray-100 text-gray-500 border-gray-200' },
  };
  const c = config[status] || config.none;
  return <Badge className={c.className}>{c.label}</Badge>;
};

const TrackingTimeline = ({ currentStatus, statusHistory }) => {
  const currentIndex = TRACKING_STEPS.findIndex(s => s.key === currentStatus);
  const historyMap = {};
  (statusHistory || []).forEach(h => { historyMap[h.status] = h; });

  return (
    <div className="py-4" data-testid="tracking-timeline">
      {TRACKING_STEPS.map((step, index) => {
        const isCompleted = index < currentIndex;
        const isCurrent = index === currentIndex;
        const isPending = index > currentIndex;
        const historyEntry = historyMap[step.key];

        return (
          <div key={step.key} className="flex items-start gap-4 relative" data-testid={`tracking-step-${step.key}`}>
            {index < TRACKING_STEPS.length - 1 && (
              <div className={`absolute left-[11px] top-[28px] w-0.5 h-[calc(100%-4px)] ${isCompleted ? 'bg-[#C6A87C]' : 'bg-gray-200'}`} />
            )}
            <div className="flex-shrink-0 relative z-10">
              {isCompleted ? (
                <CheckCircle className="w-6 h-6 text-[#C6A87C]" />
              ) : isCurrent ? (
                <CircleDot className="w-6 h-6 text-[#C6A87C] animate-pulse" />
              ) : (
                <Circle className="w-6 h-6 text-gray-300" />
              )}
            </div>
            <div className={`pb-6 ${isPending ? 'opacity-40' : ''}`}>
              <p className={`text-sm font-semibold ${isCurrent ? 'text-[#C6A87C]' : ''}`} style={{ color: isPending ? '#999' : (isCurrent ? undefined : '#333') }}>
                {step.label}
              </p>
              <p className="text-xs" style={{ color: '#999' }}>{step.desc}</p>
              {historyEntry && (
                <p className="text-xs mt-1" style={{ color: '#666' }}>
                  {new Date(historyEntry.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  {historyEntry.note && ` — ${historyEntry.note}`}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const OrderStatusPage = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [orderId, setOrderId] = useState(searchParams.get('order_id') || '');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);

  // Auto-search if URL params present
  useEffect(() => {
    const e = searchParams.get('email');
    const oid = searchParams.get('order_id');
    if (e || oid) {
      handleSearch(e, oid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = async (searchEmail, searchOrderId) => {
    const e = searchEmail ?? email;
    const oid = searchOrderId ?? orderId;
    if (!e && !oid) return;

    setLoading(true);
    setSearched(true);
    try {
      const params = new URLSearchParams();
      if (e) params.set('email', e);
      if (oid) params.set('order_id', oid);

      const response = await fetch(`${API}/orders/lookup?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setOrders(data);
        if (data.length === 1) setExpandedOrder(data[0].session_id);
      } else {
        setOrders([]);
      }
    } catch (err) {
      console.error('Lookup error:', err);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="order-status-page">
      <div className="px-6 md:px-12 py-12">
        <div className="max-w-[900px] mx-auto">
          {/* Header */}
          <div className="mb-10">
            <p className="text-sm text-[#C6A87C] uppercase tracking-widest mb-4">Order Status</p>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight" style={{ color: '#333333' }}>
              Track Your Order
            </h1>
            <p className="mt-2" style={{ color: '#666666' }}>Look up your rental order by email or order ID</p>
          </div>

          {/* Search Form */}
          <div className="bg-gray-50 border border-gray-200 p-6 mb-8" data-testid="order-lookup-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs text-[#C6A87C] uppercase tracking-widest mb-2 block font-semibold">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your checkout email"
                  className="w-full h-12 px-4 bg-white border border-gray-200 placeholder:text-gray-400 focus:border-[#C6A87C] focus:outline-none focus:ring-1 focus:ring-[#C6A87C] transition-colors"
                  style={{ color: '#333333' }}
                  data-testid="lookup-email-input"
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <div>
                <label className="text-xs text-[#C6A87C] uppercase tracking-widest mb-2 block font-semibold">Order ID (optional)</label>
                <input
                  type="text"
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                  placeholder="e.g. order_abc123"
                  className="w-full h-12 px-4 bg-white border border-gray-200 placeholder:text-gray-400 focus:border-[#C6A87C] focus:outline-none focus:ring-1 focus:ring-[#C6A87C] transition-colors"
                  style={{ color: '#333333' }}
                  data-testid="lookup-order-id-input"
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
            </div>
            <Button
              onClick={() => handleSearch()}
              disabled={!email && !orderId}
              className="bg-[#1a1a1a] text-white hover:bg-[#333] font-bold uppercase tracking-wider"
              data-testid="lookup-search-btn"
            >
              <Search className="w-4 h-4 mr-2" />
              Look Up Order
            </Button>
          </div>

          {/* Results */}
          {loading && (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-4 text-sm" style={{ color: '#666' }}>Looking up your order...</p>
            </div>
          )}

          {searched && !loading && orders.length === 0 && (
            <div className="bg-gray-50 border border-gray-200 p-12 text-center">
              <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2" style={{ color: '#333333' }}>No Orders Found</h3>
              <p className="mb-6" style={{ color: '#666666' }}>
                We couldn't find any orders matching your search. Please check the email or order ID and try again.
              </p>
              <Link to="/products">
                <Button className="bg-[#1a1a1a] text-white hover:bg-[#333] font-bold uppercase tracking-wider">
                  Browse Gear <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          )}

          {orders.length > 0 && (
            <div className="space-y-4" data-testid="order-results">
              <p className="text-sm font-semibold uppercase tracking-widest" style={{ color: '#999' }}>
                {orders.length} order{orders.length > 1 ? 's' : ''} found
              </p>

              {orders.map((order) => {
                const isExpanded = expandedOrder === order.session_id;
                const createdDate = order.created_at ? new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';
                const itemNames = order.items?.map(i => i.title).join(', ') || 'Rental Order';

                return (
                  <div key={order.session_id} className="bg-white border border-gray-200 hover:border-[#C6A87C]/30 transition-colors" data-testid={`order-result-${order.session_id}`}>
                    {/* Header */}
                    <div
                      className="p-5 flex flex-col sm:flex-row sm:items-center gap-4 cursor-pointer"
                      onClick={() => setExpandedOrder(isExpanded ? null : order.session_id)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <CreditCard className="w-4 h-4 text-[#C6A87C] flex-shrink-0" />
                          <p className="font-['Barlow_Condensed'] text-lg font-semibold uppercase tracking-tight truncate" style={{ color: '#333333' }}>
                            {itemNames}
                          </p>
                        </div>
                        <p className="text-xs ml-7" style={{ color: '#999999' }}>
                          {createdDate} &middot; {order.rental_days} day{order.rental_days > 1 ? 's' : ''} rental
                        </p>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {order.deposit_amount > 0 && <DepositBadge status={order.deposit_status} />}
                        <span className="font-['Barlow_Condensed'] text-xl font-bold text-[#C6A87C]">${order.amount?.toFixed(2)}</span>
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="border-t border-gray-100 px-5 pb-5 pt-4 space-y-4">
                        {/* Tracking Timeline */}
                        {order.order_status && (
                          <div className="bg-gray-50 border border-gray-200 p-4">
                            <h3 className="font-['Barlow_Condensed'] text-sm font-semibold uppercase tracking-widest mb-2 flex items-center gap-2" style={{ color: '#333' }}>
                              <Truck className="w-4 h-4 text-[#C6A87C]" /> Order Tracking
                            </h3>
                            <TrackingTimeline currentStatus={order.order_status} statusHistory={order.status_history} />
                          </div>
                        )}

                        {/* Rental dates */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#999999' }}>Start Date</p>
                            <p className="font-semibold" style={{ color: '#333333' }}>{order.rental_start}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#999999' }}>End Date</p>
                            <p className="font-semibold" style={{ color: '#333333' }}>{order.rental_end}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#999999' }}>Return By</p>
                            <p className="font-semibold text-[#C6A87C]">{order.return_date}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#999999' }}>Days</p>
                            <p className="font-semibold" style={{ color: '#333333' }}>{order.rental_days}</p>
                          </div>
                        </div>

                        {/* Price breakdown */}
                        <div className="bg-gray-50 p-4 space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span style={{ color: '#666666' }}>Rental Subtotal</span>
                            <span style={{ color: '#333333' }}>${order.rental_subtotal?.toFixed(2)}</span>
                          </div>
                          {order.discount_amount > 0 && (
                            <div className="flex justify-between text-[#C6A87C]">
                              <span>Discount ({order.discount_percent}%)</span>
                              <span>-${order.discount_amount?.toFixed(2)}</span>
                            </div>
                          )}
                          {order.shipping_fee > 0 && (
                            <div className="flex justify-between">
                              <span style={{ color: '#666666' }}>Expedited Shipping</span>
                              <span style={{ color: '#333333' }}>${order.shipping_fee?.toFixed(2)}</span>
                            </div>
                          )}
                          {order.insurance_total > 0 && (
                            <div className="flex justify-between">
                              <span style={{ color: '#666666' }}>Damage Protection</span>
                              <span style={{ color: '#333333' }}>${order.insurance_total?.toFixed(2)}</span>
                            </div>
                          )}
                          {order.deposit_amount > 0 && (
                            <div className="flex justify-between">
                              <span className="text-green-700 flex items-center gap-1"><Shield className="w-3 h-3" /> Security Deposit</span>
                              <span className="text-green-700">${order.deposit_amount?.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between pt-2 border-t border-gray-200 font-semibold">
                            <span style={{ color: '#333333' }}>Total</span>
                            <span className="text-[#C6A87C]">${order.amount?.toFixed(2)}</span>
                          </div>
                        </div>

                        {/* Deposit detail */}
                        {order.deposit_amount > 0 && (
                          <div className={`p-3 border text-sm flex items-center gap-3 ${
                            order.deposit_status === 'refunded' ? 'bg-green-50 border-green-200' :
                            order.deposit_status === 'forfeited' ? 'bg-red-50 border-red-200' :
                            'bg-amber-50 border-amber-200'
                          }`}>
                            <Shield className={`w-5 h-5 flex-shrink-0 ${
                              order.deposit_status === 'refunded' ? 'text-green-600' :
                              order.deposit_status === 'forfeited' ? 'text-red-600' : 'text-amber-600'
                            }`} />
                            <div>
                              <div className="font-semibold" style={{ color: '#333333' }}>
                                ${order.deposit_amount?.toFixed(2)} Security Deposit — <DepositBadge status={order.deposit_status} />
                              </div>
                              <p className="text-xs mt-0.5" style={{ color: '#666666' }}>
                                {order.deposit_status === 'held' && 'Your deposit will be refunded after gear is returned and inspected.'}
                                {order.deposit_status === 'refunded' && 'Your deposit has been refunded to your original payment method.'}
                                {order.deposit_status === 'under_review' && 'Your gear is being inspected. We\'ll update you shortly.'}
                                {order.deposit_status === 'forfeited' && 'Deposit was applied due to damage or loss. Contact us for details.'}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Shipping info */}
                        {order.shipping_info && (
                          <div className="flex items-start gap-2 text-sm">
                            <MapPin className="w-4 h-4 text-[#C6A87C] mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="font-semibold" style={{ color: '#333333' }}>{order.shipping_info.recipient_name}</p>
                              <p style={{ color: '#666666' }} className="capitalize">{order.shipping_info.destination_type} &middot; {order.shipping_info.full_address}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderStatusPage;
